import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { PageStateListItem } from './PageStateListItem';
import { Namespace } from '../../../translation/namespace';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { ButtonType } from '../../../types/ButtonType';
import useCollapsibleOverlay from '../../../hooks/useCollapsibleOverlay';
import { CollapsibleModifier } from '../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay.modifier';
import { Icons } from '../../../components/atoms/SVG';
import { PageManagementBadge } from './PageManagementBadge';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import extendClassName from '../../../lib/bemUtils/bem';
import { CollapsibleContent } from '../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay';
import { PagePackStates } from '../../../types/api/PagePackState';
import ILink from '../../../types/ILink';
import { VersionStates } from '../../../types/api/VersionState';

export type PageStateProps = {
  packState: PagePackStates;
  versions: { link: ILink; timestamp: string; versionState: VersionStates; importantChange: boolean }[];
  versionState: VersionStates;
  versionable: boolean;
};

const packStates = {
  onlyDraftVersion: { modifier: 'red', icon: Icons.PUBLISH_LIGHTS_RED },
  publishedWithDraftVersion: { modifier: 'yellow', icon: Icons.PUBLISH_LIGHTS_YELLOW },
  onlyPublishedVersion: { modifier: 'green', icon: Icons.PUBLISH_LIGHTS_GREEN },
  template: {
    modifier: 'purple', icon: Icons.PUBLISH_LIGHTS_RED, // Red lights because they look the same here but disabled
  },
};

// This can be ignored, since we're using an object with hardcoded properties.
// Otherwise, use `Object.prototype.hasOwnProperty.call(packStates, state)`
// eslint-disable-next-line no-prototype-builtins
const validPackState = (state: PagePackStates) => packStates.hasOwnProperty(state);

/**
 * This component communicates the "PageState" via
 * 1. a badge presenting the `versionState` and
 * 2. an indicator representing the `packState`
 */
export default function PageState({ versionState, packState, versions, versionable }: PageStateProps) {
  const { t } = useTranslation(Namespace.PageState);
  const { pathname } = useLocation();

  const { collapsibleOpen, toggleProps, collapsibleProps } = useCollapsibleOverlay();
  if (!versionState || !validPackState(packState)) {
    // logging appreciated?
    return null;
  }

  const isTemplate = packState === 'template' && versionState === 'template';

  const dfnText = t(`${packState}.dfn`);
  let btnModifier;

  if (!versionable && !isTemplate) {
    btnModifier = BtnModifier.WHITE_BACKGROUND_FOR_DISABLED_PAGE_STATE;
  } else if (collapsibleOpen) {
    btnModifier = BtnModifier.ACTIVE;
  }

  const getButtonName = () => {
    if (versionable) {
      return t('button.text');
    } else {
      if (isTemplate) {
        return t('button.templateText');
      }
      return t('button.notVersionedText');
    }
  };

  return (
    <div className="page-state">
      {versionable || (isTemplate)
        ? (
            <PageManagementBadge badgeType={versionState}>
              <dfn className="invisible">{dfnText}: </dfn>
              <span className="invisible"> {t(`${versionState}.${packState}`)}</span>
              <span aria-hidden="true" data-testid="badge">
                {t(`versionState.${versionState}`)}
              </span>
            </PageManagementBadge>
          )
        : null}
      <div className="page-state__button-container">
        <Btn
          reference={toggleProps.ref}
          onClick={versionable ? toggleProps.onClick : undefined}
          onKeyDown={toggleProps.onKeyDown}
          onKeyUp={toggleProps.onKeyUp}
          tooltipPlacement={TooltipPlacement.TOP}
          type={ButtonType.BUTTON}
          svgSpriteId={collapsibleOpen ? Icons.DECLINE_CROSS : packStates[packState].icon}
          text={getButtonName()}
          titleText={getButtonName()}
          hiddenText
          ariaControls={versionable ? toggleProps['aria-controls'] : undefined}
          ariaExpanded={versionable ? toggleProps['aria-expanded'] : undefined}
          modifier={btnModifier}
          ariaDisabled={!versionable}
        />
        <span className={extendClassName('page-state__indicator', packStates[packState].modifier)} />
        {versionable && versions && versions.length > 0 && (
          <CollapsibleContent
            {...collapsibleProps}
            modifier={
              versionState === 'publishedHistory'
                ? CollapsibleModifier.PAGE_STATE_PUBLISHED
                : CollapsibleModifier.PAGE_STATE
            }
          >
            {/* this container is scrollable */}
            <div className="page-state__list-container">
              <ul className="page-state__list">
                {versions.map(el => (
                  <li key={el.link.url}>
                    <PageStateListItem active={pathname.endsWith(el.link.url)} {...el} />
                  </li>
                ))}
              </ul>
            </div>
          </CollapsibleContent>
        )}
      </div>
    </div>
  );
}
